import type { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type {
  TextInputComponentType,
  TextInputStateType,
} from '@unifyapps/defs/blocks/TextInput/types';
import { withSuspense as withBlockSuspense } from '@unifyapps/carbon/no-code/components/withSuspense';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { Input } from '@unifyapps/ui/components/Input';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { Typography } from '@unifyapps/ui/components/Typography';
import { useGetDeviceDetailsInStateStore } from '@unifyapps/carbon/no-code/stores/GlobalStateStore';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { getConditionalPropertyValue } from '@unifyapps/carbon/utils/conditionalValues';
import type { TypographyColors } from '@unifyapps/defs/types/typography';
import { clsx } from 'clsx';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { useMountEffect, usePrevious } from '@react-hookz/web';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { Stack } from '@unifyapps/ui/components/Stack';
import { ComputeContextHelper } from '@unifyapps/carbon/no-code/hooks/computeContext/ComputeContextHelper';
import {
  placeholderAlignmentToClassName,
  placeholderColorToClassName,
  placeholderWeightToClassName,
} from './constants';
import { useEndDecorator } from './useEndDecorator';

function TextInput({
  events,
  blockState,
  attributesAndEventListeners,
  updateBlockState,
  getGlobalStoreContext,
  dndRef,
  computedBlockState: computedComponent,
  blockRef,
}: BlockComponentProps<TextInputComponentType, TextInputStateType, TextInputStateType>) {
  const inputRef = blockRef as RefObject<HTMLInputElement>;
  const methodsRef = useRef<{ onInputChange?: (newInput: string | undefined) => void }>(null);

  const { emitEvent } = useBlockEvents(events);

  const { device } = useGetDeviceDetailsInStateStore();
  const pageMode = useInterfaceStore().use.mode();

  const previousDefaultValue = usePrevious(computedComponent.content.defaultValue);

  const { content, appearance } = computedComponent;
  const { defaultValue, placeholder, addOns } = content;
  const { label, caption, prefixIcon, suffixIcon, dictation } = addOns ?? {};
  const {
    placeholder: placeholderAppearance,
    variant,
    size,
    styles,
    value: valueAppearance,
    disabled,
    inputBox,
    autoFocus,
  } = appearance ?? {};

  const { appearance: labelAppearance } = label ?? {};
  const { variant: labelVariant, weight: labelWeight, color: _labelColor } = labelAppearance ?? {};
  const labelColor =
    getConditionalPropertyValue<TypographyColors>({ value: _labelColor }) || 'text-primary';

  const { appearance: captionAppearance } = caption ?? {};
  const {
    variant: captionVariant,
    weight: captionWeight,
    color: _captionColor,
  } = captionAppearance ?? {};
  const captionColor =
    getConditionalPropertyValue<TypographyColors>({ value: _captionColor }) || 'text-tertiary';

  const { variant: textVariant, weight: valueWeight, color: valueColor } = valueAppearance ?? {};
  const {
    weight: placeholderWeight,
    color: placeholderColor,
    align: placeholderAlignment,
  } = placeholderAppearance ?? {};

  const PrefixIcon = getIconFromRegistry(prefixIcon?.name);

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: styles,
  });

  const { className: inputClassName, style: inputStyle } = useBlockAppearanceStyles({
    appearanceStyles: {
      backgroundColor: styles?.backgroundColor,
      padding: inputBox?.padding,
    },
  });

  useMountEffect(() => {
    updateBlockState((draft) => {
      draft.value = defaultValue;
    });
  });

  /**
   * if the user has triggered reset value block method,  we are setting to default value if user opted for reset to default method
   * and set resetToDefault undefined
   */
  useEffect(() => {
    if (blockState.resetToDefault !== undefined) {
      updateBlockState((draft) => {
        if (blockState.resetToDefault) {
          // user opted for reset to default so we are setting it here
          draft.value = defaultValue;
        } else {
          // user opted for complete reset so we are setting it to undefined
          draft.value = undefined;
        }

        draft.resetToDefault = undefined;
      });
    }
  }, [defaultValue, updateBlockState, blockState.resetToDefault]);

  /**
   * when we are in builder mode and if we have changed default value we are setting the value to new default value
   */
  useEffect(() => {
    if (pageMode === InterfaceModes.BUILDER && defaultValue !== previousDefaultValue) {
      updateBlockState((draft) => {
        draft.value = defaultValue;
      });
    }
  }, [defaultValue, updateBlockState, previousDefaultValue, pageMode, blockState]);

  const onInputChange = useEventCallback(
    (newInput: string, event?: ChangeEvent<HTMLInputElement>) => {
      methodsRef.current?.onInputChange?.(newInput);

      updateBlockState((draft) => {
        draft.value = newInput;
      });

      void emitEvent({
        eventType: EventTypeEnum.OnChange,
        actionContext: { value: newInput },
        domEvent: event,
      });
    },
  );

  const onChange = useEventCallback((event: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = event.target.value;

    onInputChange(updatedValue);
  });

  const onKeyDown = useEventCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      void emitEvent({ eventType: EventTypeEnum.OnSubmit, domEvent: event });
    }
  });

  const onFocus = useCallback(
    (event: SyntheticEvent) => {
      void emitEvent({ eventType: EventTypeEnum.OnFocus, domEvent: event });
      updateBlockState((draft) => {
        draft.isFocused = true;
      });
    },
    [emitEvent, updateBlockState],
  );

  const onBlur = useCallback(
    (event: SyntheticEvent) => {
      void emitEvent({ eventType: EventTypeEnum.OnBlur, domEvent: event });
      updateBlockState((draft) => {
        draft.isFocused = false;
      });
    },
    [emitEvent, updateBlockState],
  );

  const slotProps = useMemo(
    () => ({
      input: {
        className: placeholderAlignment
          ? placeholderAlignmentToClassName[placeholderAlignment]
          : undefined,
      },
    }),
    [placeholderAlignment],
  );

  const valueAndPlaceholderClassNames = clsx(
    textVariant,
    valueWeight,
    valueColor ? `!${valueColor}` : valueColor,
    placeholderColor ? (placeholderColorToClassName[placeholderColor] as string) : '',
    placeholderWeight ? (placeholderWeightToClassName[placeholderWeight] as string) : '',
  );

  const context = getGlobalStoreContext();
  const { locale } = ComputeContextHelper.getUserContext(context) || {};

  const { endDecoratorComponent, endDecoratorNode } = useEndDecorator({
    suffixIcon,
    allowDictation: Boolean(dictation),
    input: blockState.value,
    onInputChange,
    inputRef,
    methodsRef,
    size,
    locale,
    dictation,
  });

  return (
    <Stack
      className={clsx(size === 'sm' ? 'gap-y-xs' : 'gap-y-sm', className)}
      style={style}
      {...attributesAndEventListeners}
      ref={dndRef}
    >
      {label?.value ? (
        <Typography as="div" className={labelColor} variant={labelVariant} weight={labelWeight}>
          {label.value}
        </Typography>
      ) : null}
      {device === DeviceVariantType.DESKTOP && caption?.value ? (
        <Typography
          as="div"
          className={captionColor}
          variant={captionVariant}
          weight={captionWeight}
        >
          {caption.value}
        </Typography>
      ) : null}
      <Input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- needed
        autoFocus={autoFocus}
        className={clsx(
          inputClassName,
          device !== DeviceVariantType.DESKTOP
            ? `focus-within:!ring-0 ${valueAndPlaceholderClassNames}`
            : valueAndPlaceholderClassNames,
        )}
        defaultValue={defaultValue}
        disabled={disabled}
        endDecoratorComponent={endDecoratorComponent}
        endDecoratorNode={endDecoratorNode}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        size={inputBox?.padding ? 'custom' : size}
        slotProps={slotProps}
        startDecoratorComponent={PrefixIcon}
        style={inputStyle}
        value={blockState.value}
        variant={variant}
      />
      {device !== DeviceVariantType.DESKTOP && caption?.value ? (
        <Typography
          as="div"
          className={captionColor}
          variant={captionVariant}
          weight={captionWeight}
        >
          {caption.value}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default memo(withBlockSuspense(TextInput));
